<template>
  <b-modal
    id="modal-importar-csv"
    v-model="show"
    title="Importar pedido por CSV"
    size="lg"
    centered
    no-close-on-backdrop
    :no-close-on-esc="processando"
    :hide-header-close="processando"
    hide-footer
    @hidden="onHidden"
  >
    <!-- Processando: adicionando itens ao carrinho -->
    <div v-if="processando" class="text-center py-2">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <h5 class="mt-2 mb-1">Adicionando itens ao carrinho...</h5>
      <p class="text-muted mb-0">Calculando impostos e preparando o carrinho. Não feche esta janela.</p>
    </div>

    <!-- Etapa 1: seleção do arquivo -->
    <div v-else-if="!preview">
      <b-alert variant="info" show class="mb-2">
        <div class="alert-body">
          <p class="mb-1">
            Selecione um arquivo <strong>CSV</strong> com um produto por linha no formato
            <code>código;quantidade</code>. O código é o <strong>código interno do produto</strong> (ISBN/EAN).
          </p>
          <ul class="mb-0 pl-3">
            <li><code>9786685765496;2</code></li>
            <li><code>9786685766004;2</code></li>
          </ul>
        </div>
      </b-alert>

      <b-alert v-if="!contextoValido" variant="warning" show class="mb-2">
        <div class="alert-body">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          Selecione primeiro o <strong>cliente e a tabela de preço</strong> do pedido para importar.
        </div>
      </b-alert>

      <b-form-group label="Arquivo CSV" label-for="csv-file-input">
        <input
          id="csv-file-input"
          ref="fileInput"
          type="file"
          class="form-control"
          accept=".csv,text/csv"
          :disabled="analisando || !contextoValido"
          @change="onSelect"
        >
      </b-form-group>

      <div v-if="linhasValidas.length" class="mb-2">
        <b-badge variant="light-primary">
          {{ linhasValidas.length }} linha(ns) válida(s)
        </b-badge>
        <b-badge v-if="linhasInvalidas" variant="light-warning" class="ml-50">
          {{ linhasInvalidas }} linha(s) ignorada(s)
        </b-badge>
      </div>

      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" :disabled="analisando" @click="show = false">
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="analisando || !linhasValidas.length || !contextoValido"
          @click="analisar"
        >
          <b-spinner v-if="analisando" small class="mr-50" />
          <feather-icon v-else icon="SearchIcon" class="mr-50" />
          Analisar {{ linhasValidas.length ? `(${linhasValidas.length})` : '' }}
        </b-button>
      </div>
    </div>

    <!-- Etapa 2: revisão antes de adicionar ao carrinho -->
    <div v-else>
      <h5 class="mb-1">Confira os itens</h5>
      <b-row class="mb-1">
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-success">{{ preview.resumo.encontrados }}</h3>
            <small class="text-muted">Encontrados</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-warning">{{ preview.resumo.semPreco }}</h3>
            <small class="text-muted">Sem preço</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-danger">{{ preview.resumo.naoEncontrados }}</h3>
            <small class="text-muted">Não encontrados</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0">{{ preview.resumo.quantidadeTotal }}</h3>
            <small class="text-muted">Qtde total</small>
          </b-card>
        </b-col>
      </b-row>

      <div class="border rounded" style="max-height: 320px; overflow-y: auto;">
        <b-table-simple small responsive class="mb-0">
          <b-thead head-variant="light">
            <b-tr>
              <b-th>Código</b-th>
              <b-th>Produto</b-th>
              <b-th class="text-center d-none">Categoria</b-th>
              <b-th class="text-center">Qtde</b-th>
              <b-th class="text-right">Preço</b-th>
              <b-th class="text-center">Situação</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(p, i) in preview.itens" :key="`csv-${i}`">
              <b-td><code>{{ p.codigo }}</code></b-td>
              <b-td>
                <span v-if="p.status !== 'nao_encontrado'">
                  {{ p.item_descricao || p.familia_comercial_descricao }}
                  <small class="text-muted d-block">{{ p.marca }}</small>
                </span>
                <small v-else class="text-muted">Sem produto correspondente</small>
              </b-td>
              <b-td class="text-center d-none">
                <template v-if="Array.isArray(p.categorias) && p.categorias.length">
                  <b-badge
                    v-for="cat in p.categorias"
                    :key="`cat-${i}-${cat}`"
                    :variant="categoriaVariant(cat)"
                    class="mr-25"
                  >
                    {{ nomeCategoria(cat) }}
                  </b-badge>
                </template>
                <span v-else class="text-muted">—</span>
              </b-td>
              <b-td class="text-center">{{ p.quantidade }}</b-td>
              <b-td class="text-right">
                <span v-if="p.preco != null">
                  {{ p.preco | formtPriceBr }}
                </span>
                <span v-else class="text-muted">—</span>
              </b-td>
              <b-td class="text-center">
                <b-badge :variant="statusVariant(p.status)">{{ statusLabel(p.status) }}</b-badge>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-1">
        <small v-if="preview.resumo.naoEncontrados || preview.resumo.semPreco" class="text-muted">
          Apenas itens com preço e da categoria do pedido serão adicionados.
        </small>
        <span v-else />
        <div>
          <b-button variant="outline-secondary" class="mr-1" :disabled="analisando || processando" @click="voltar">
            Voltar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!adicionaveis || analisando || processando || !previewPronto"
            @click="adicionarAoCarrinho"
          >
            <feather-icon icon="ShoppingCartIcon" class="mr-50" />
            {{ contexto === 'operacao' ? 'Adicionar ao pedido' : 'Adicionar ao carrinho' }} {{ adicionaveis ? `(${adicionaveis})` : '' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Escolha de categoria quando o CSV tem itens de mais de uma categoria -->
    <b-modal
      v-model="escolhaCategoria.visivel"
      title="Qual categoria deseja usar?"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      hide-footer
      size="sm"
    >
      <p class="mb-1">
        O arquivo tem itens de <strong>mais de uma categoria</strong>. Um pedido só pode ter
        <strong>uma categoria</strong> — escolha qual usar; os itens das demais não serão adicionados.
      </p>
      <b-form-radio-group v-model="escolhaCategoria.selecionada" stacked class="mb-1">
        <b-form-radio v-for="cat in escolhaCategoria.opcoes" :key="`opc-${cat}`" :value="cat" class="mb-50">
          <b-badge :variant="categoriaVariant(cat)">{{ nomeCategoria(cat) }}</b-badge>
          <small class="text-muted ml-50">{{ escolhaCategoria.contagem[cat] || 0 }} item(ns)</small>
        </b-form-radio>
      </b-form-radio-group>
      <div class="d-flex justify-content-end">
        <b-button variant="primary" :disabled="!escolhaCategoria.selecionada" @click="confirmarEscolhaCategoria">
          Confirmar
        </b-button>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import {
  BModal, BAlert, BFormGroup, BButton, BBadge, BRow, BCol, BCard, BSpinner,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd, BFormRadioGroup, BFormRadio,
} from 'bootstrap-vue';
import { formtPriceBr } from '@core/utils/filter';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import SLS_pedidosVendasService from '@/services/Operacoes/sls_pedidoVendas.service';
import EsMarcaClient from '@/api/SistemaDKP/sls-marca.client';
import { IMAGEM_PADRAO } from '@/utils/imagemUrl';
import { simularImpostoUnitario } from '@/utils/impostoSimulador';
import { selecionarNaturezaFiscal } from '@/utils/naturezaFiscal';
import { isPerfilClienteAtivo } from '@/libs/perfilCliente';
import { validarEstoqueItens } from '@/utils/estoqueBloqueio';
import { nomeCategoria } from '@/libs/perfilCliente';
import axios from '@/libs/axios';

export default {
  name: 'ModalImportarCsv',
  components: {
    BModal, BAlert, BFormGroup, BButton, BBadge, BRow, BCol, BCard, BSpinner,
    BTableSimple, BThead, BTbody, BTr, BTh, BTd, BFormRadioGroup, BFormRadio,
  },
  filters: {
    formtPriceBr,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 'home' (carrinho do Portal B2B) ou 'operacao' (Operações → Pedidos de Venda)
    contexto: {
      type: String,
      default: 'home',
    },
  },
  data() {
    return {
      service: new SLS_pedidosVendasService(),
      linhasValidas: [],
      linhasInvalidas: 0,
      analisando: false,
      processando: false,
      sessao: 0,
      proximaRequisicao: 0,
      analiseAtiva: null,
      processamentoAtivo: null,
      preview: null,
      escolhaCategoria: {
        visivel: false,
        opcoes: [],
        contagem: {},
        selecionada: null,
        resolve: null,
      },
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    contextoValido() {
      return !!this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];
    },
    formInfoPedido() {
      return {
        ...(this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {}),
        enderecoAlternativo: this.$store.getters['InfoPedido/GET_ENDERECO_ALTERNATIVO'],
      };
    },
    precoId() {
      return this.formInfoPedido?.tabelaPreco?.tabelapreco_id || '';
    },
    adicionaveis() {
      if (!this.preview) return 0;
      return this.preview.itens.filter((p) => p.status === 'encontrado').length;
    },
    previewPronto() {
      return !!(this.preview
        && this.preview.precosValidados
        && this.contextoPermaneceValido(this.preview.contextoPreco, true));
    },
    tipoPedidoSelecionado() {
      const f = this.formInfoPedido;
      return String(
        f.tipoPedido?.tipopedidoid
          || f.tipoPedidoCliente?.tipopedidoid
          || this.$store.getters['intensPedido/getFormData']?.dataCapaPedido?.tipopedidoid
          || f.dataCapaPedido?.tipopedidoid
          || '',
      ).toUpperCase();
    },
    // Tipos de pedido permitidos ao perfil logado (lista já filtrada pelo
    // backend via GET tipo-pedido?somentePermitidos=1, carregada no InfoPedido).
    tiposPermitidosPerfil() {
      const lista = this.$store.getters['InfoPedido/GET_TIPO_PEDIDOS_CLIENTE'] || [];
      return lista
        .filter((t) => t && t.value !== null && t.value !== undefined && t.value !== '')
        .map((t) => String(t.value).toUpperCase());
    },
  },
  watch: {
    value(aberto) {
      // Invalida imediatamente (antes do evento hidden) qualquer resposta da
      // abertura anterior. Isso também cobre fechar e reabrir muito rápido.
      if (!aberto) this.invalidarSessao();
    },
  },
  methods: {
    isPerfilClienteAtivo,
    invalidarSessao() {
      this.sessao += 1;
      this.analiseAtiva = null;
      this.processamentoAtivo = null;
    },
    onSelect(event) {
      this.preview = null;
      this.linhasValidas = [];
      this.linhasInvalidas = 0;
      const file = (event.target.files || [])[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.parseCsv(String(e.target.result || ''));
      };
      reader.readAsText(file);
    },
    parseCsv(texto) {
      const linhas = texto.split(/\r?\n/);
      const validas = [];
      let invalidas = 0;
      linhas.forEach((raw) => {
        const linha = raw.replace(/"/g, '').trim();
        if (!linha) return;
        const partes = linha.split(';');
        const codigo = (partes[0] || '').trim();
        const quantidade = parseInt((partes[1] || '').trim(), 10);
        if (!codigo || !Number.isFinite(quantidade) || quantidade <= 0) {
          invalidas += 1;
          return;
        }
        validas.push({ codigo, quantidade });
      });
      this.linhasValidas = validas;
      this.linhasInvalidas = invalidas;
    },
    statusLabel(status) {
      const map = {
        encontrado: 'Adicionar',
        sem_preco: 'Sem preço',
        imposto_erro: 'Imposto não calculado',
        nao_encontrado: 'Não encontrado',
        categoria_diferente: 'Categoria diferente',
        sem_estoque: 'Sem estoque',
      };
      return map[status] || status;
    },
    statusVariant(status) {
      const map = {
        encontrado: 'light-success',
        sem_preco: 'light-warning',
        imposto_erro: 'light-danger',
        nao_encontrado: 'light-danger',
        categoria_diferente: 'light-danger',
        sem_estoque: 'light-danger',
      };
      return map[status] || 'light-secondary';
    },
    nomeCategoria,
    categoriaVariant(cat) {
      const map = {
        WS: 'light-primary',
        WD: 'light-success',
        WC: 'light-info',
      };
      return map[String(cat || '').toUpperCase()] || 'light-secondary';
    },
    capturarContexto(incluirTipo = true) {
      const form = this.formInfoPedido;
      const endereco = this.$store.getters['InfoPedido/GET_ENDERECO_ALTERNATIVO'] || form?.enderecoAlternativo;
      const tipoPedidoFiscal = String(
        form?.tipoPedidoCliente?.tipopedidoid
          || form?.dataCapaPedido?.tipopedidoid
          || form?.tipoPedido?.tipopedidoid
          || '',
      ).toUpperCase();
      return {
        sessao: this.sessao,
        estabelecimentoId: form?.estabelecimento?.estabelecimentoid || '',
        clienteId: form?.cliente?.clienteid || '',
        clienteNomeAbreviado: form?.cliente?.clientenomeabreviado || '',
        tabelaPrecoId: form?.tabelaPreco?.tabelapreco_id
          || form?.dataCapaPedido?.tabelaprecoid
          || '',
        condicaoPagamentoId: Number(form?.condicaoPagamento?.condicaopagamentoid || 0),
        naturezaOperacaoId: [
          form?.cliente?.clientenatoperinterna,
          form?.cliente?.clientenatoperexterna,
          form?.estabelecimento?.ufid,
        ].join('|'),
        canalVendaId: form?.canalVenda?.canal_venda_id
          || form?.cliente?.canalvendaid
          || form?.cliente?.canal_venda_id
          || form?.dataCapaPedido?.canalvendaid
          || '',
        uf: endereco?.uf || form?.enderecoEntrega?.ufid || '',
        cidade: endereco?.cidade || form?.enderecoEntrega?.clienteenderecocidade || '',
        contribuinteIcms: form?.cliente?.clienteisentoie ? 0 : 1,
        tipoPedidoSelecionado: incluirTipo ? this.tipoPedidoSelecionado : null,
        tipoPedidoFiscal: incluirTipo ? tipoPedidoFiscal : null,
        pedidoDoacao: incluirTipo ? tipoPedidoFiscal === 'WD' : null,
        tenant: typeof localStorage !== 'undefined' ? (localStorage.getItem('tenantSlug') || '') : '',
      };
    },
    contextoPermaneceValido(contexto, incluirTipo = true) {
      if (!this.value || !contexto || contexto.sessao !== this.sessao) return false;
      const atual = this.capturarContexto(incluirTipo);
      return Object.keys(contexto).every((chave) => contexto[chave] === atual[chave]);
    },
    precoValidado(valor) {
      const numero = Number(valor);
      return Number.isFinite(numero) && numero > 0 ? numero : null;
    },
    marcarErroImposto(item, preview = this.preview) {
      if (!item || item.status !== 'encontrado') return;
      this.$set(item, 'preco', null);
      this.$set(item, 'status', 'imposto_erro');
      if (preview && preview.resumo) {
        preview.resumo.encontrados = Math.max(0, Number(preview.resumo.encontrados || 0) - 1);
        preview.resumo.semPreco = Number(preview.resumo.semPreco || 0) + 1;
      }
    },
    notificarImpostoIndisponivel() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Imposto não calculado',
          text: 'Não foi possível calcular o preço final de todos os itens. Volte e analise o arquivo novamente.',
          icon: 'AlertCircleIcon',
          variant: 'danger',
        },
      });
    },
    // Regra de categoria — TODOS os perfis (um pedido = UMA categoria, mesma
    // regra da home/ModalEmbalagens e do Novo Pedido):
    // 1. Busca as categorias (Venda WS / Doação WD / Certificação WC) dos itens
    //    e anexa em p.categorias para exibição na revisão.
    // 2. Cruza as categorias presentes no CSV com os tipos de pedido permitidos
    //    ao perfil logado (ex.: Representante só WD).
    // 3. Uma categoria compatível → usa direto (ajusta o tipo do pedido e avisa).
    //    Mais de uma → pergunta ao usuário qual usar. Itens fora da categoria
    //    escolhida são marcados como "Categoria diferente" e não são adicionados.
    async marcarCategoriasIncompativeis(preview) {
      const comItem = (preview.itens || []).filter((p) => p.item_id);
      if (!comItem.length) return;
      let flagsMap = {};
      try {
        const ids = [...new Set(comItem.map((p) => p.item_id))];
        const { data: catResp } = await axios.post('items/categorias', {
          itemIds: ids,
          clienteid: this.formInfoPedido?.cliente?.clienteid,
        });
        flagsMap = catResp?.flags || {};
      } catch (e) {
        console.warn('[ModalImportarCsv] categorias dos itens indisponíveis:', e?.message);
        return;
      }
      comItem.forEach((p) => {
        const flags = flagsMap[p.item_id];
        this.$set(p, 'categorias', Array.isArray(flags) ? flags : []);
      });

      const encontrados = comItem.filter((p) => p.status === 'encontrado');
      if (!encontrados.length) return;

      let alvo = this.tipoPedidoSelecionado;
      // Lista de tipos permitidos ao perfil ainda não carregada → não dá para
      // oferecer troca de categoria com segurança; mantém o comportamento
      // estrito de bloquear contra o tipo já selecionado no InfoPedido.
      // Vale para os DOIS contextos: home (carrinho B2B) e operação (Pedidos
      // de Venda) — a capa da operação também usa o InfoPedido.
      const permitidos = this.tiposPermitidosPerfil;
      if (permitidos.length) {
        // Categorias presentes no CSV ∩ tipos permitidos ao perfil logado
        const contagem = {};
        encontrados.forEach((p) => {
          (p.categorias || []).forEach((c) => {
            const cat = String(c).toUpperCase();
            if (permitidos.includes(cat)) {
              contagem[cat] = (contagem[cat] || 0) + 1;
            }
          });
        });
        const presentes = Object.keys(contagem);

        if (!presentes.length) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nenhum item compatível',
              text: 'Nenhum item do arquivo pertence às categorias de pedido permitidas ao seu perfil.',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          });
        } else if (presentes.length === 1) {
          [alvo] = presentes;
          if (alvo !== this.tipoPedidoSelecionado) {
            this.aplicarTipoPedido(alvo);
            this.$toast({
              component: ToastificationContent,
              props: {
                title: `Pedido de ${nomeCategoria(alvo)}`,
                text: `Os itens do arquivo são da categoria ${nomeCategoria(alvo)} — o tipo do pedido foi ajustado automaticamente.`,
                icon: 'InfoIcon',
                variant: 'info',
              },
            });
          }
        } else {
          alvo = await this.aguardarEscolhaCategoria(presentes, contagem);
          if (alvo && alvo !== this.tipoPedidoSelecionado) this.aplicarTipoPedido(alvo);
        }
      }
      if (!alvo) return;

      let bloqueados = 0;
      encontrados.forEach((p) => {
        const flags = flagsMap[p.item_id];
        if (Array.isArray(flags) && flags.length > 0 && !flags.includes(alvo)) {
          p.status = 'categoria_diferente';
          bloqueados += 1;
        }
      });
      if (bloqueados > 0) {
        if (preview.resumo) {
          preview.resumo.encontrados = Math.max(0, Number(preview.resumo.encontrados || 0) - bloqueados);
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Categoria diferente',
            text: `${bloqueados} item(ns) de categoria diferente de ${nomeCategoria(alvo)} `
              + 'não serão adicionados. Para incluí-los, finalize este pedido e faça um novo pedido da categoria deles.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
      }
    },
    // Abre a janelinha de escolha de categoria e aguarda a confirmação.
    aguardarEscolhaCategoria(opcoes, contagem) {
      return new Promise((resolve) => {
        const atual = this.tipoPedidoSelecionado;
        this.escolhaCategoria = {
          visivel: true,
          opcoes,
          contagem: contagem || {},
          selecionada: opcoes.includes(atual) ? atual : null,
          resolve,
        };
      });
    },
    confirmarEscolhaCategoria() {
      const { selecionada, resolve } = this.escolhaCategoria;
      this.escolhaCategoria = {
        visivel: false, opcoes: [], contagem: {}, selecionada: null, resolve: null,
      };
      if (typeof resolve === 'function') resolve(selecionada);
    },
    // Ajusta o Tipo de Pedido do InfoPedido para a categoria escolhida.
    // Só aplica valores presentes na lista de permitidos do perfil — nunca
    // grava um tipo "cru" que o backend não autorizou para este usuário.
    aplicarTipoPedido(cat) {
      const lista = this.$store.getters['InfoPedido/GET_TIPO_PEDIDOS_CLIENTE'] || [];
      const opc = lista.find((t) => String(t?.value || '').toUpperCase() === cat);
      if (!opc) return;
      this.$store.commit('InfoPedido/SET_TIPO_PEDIDO_CLIENTE', {
        tipopedidoid: opc.value,
        tipopedidodescricao: opc.text,
      });
      // Contexto Operações: sincroniza também a capa do pedido (fallback usado
      // pelo createPedidos quando o InfoPedido não tem tipo selecionado).
      if (this.contexto === 'operacao') {
        this.$store.commit('intensPedido/SET_FORM_DATA', {
          dataCapaPedido: { tipopedidoid: opc.value },
        });
      }
    },
    async analisar() {
      if (this.analisando || this.processando || !this.linhasValidas.length || !this.precoId) return;
      const requisicao = ++this.proximaRequisicao;
      const contextoInicial = this.capturarContexto(false);
      this.analiseAtiva = requisicao;
      this.analisando = true;
      try {
        selecionarNaturezaFiscal(this.formInfoPedido, {
          endereco: this.$store.getters['InfoPedido/GET_ENDERECO_ALTERNATIVO']
            || this.formInfoPedido?.enderecoEntrega,
        });
        const resp = await this.service.analisarImportacaoCsv(this.precoId, this.linhasValidas);
        if (this.analiseAtiva !== requisicao || !this.contextoPermaneceValido(contextoInicial, false)) return;
        const preview = (resp && resp.data) || { itens: [], resumo: {} };
        this.$set(preview, 'precosValidados', false);
        await this.marcarCategoriasIncompativeis(preview);
        // A seleção de categoria acima altera intencionalmente o tipo do pedido.
        // Por isso, o contexto completo só é fixado depois dessa etapa.
        if (this.analiseAtiva !== requisicao || !this.contextoPermaneceValido(contextoInicial, false)) return;
        const contextoPreco = this.capturarContexto(true);
        await this.marcarSemEstoque(preview);
        if (this.analiseAtiva !== requisicao || !this.contextoPermaneceValido(contextoPreco, true)) return;
        const precosValidados = await this.simularPrecosPreview(preview, contextoPreco, requisicao);
        if (this.analiseAtiva !== requisicao || !this.contextoPermaneceValido(contextoPreco, true)) return;
        this.$set(preview, 'contextoPreco', contextoPreco);
        this.$set(preview, 'precosValidados', precosValidados);
        this.preview = preview;
      } catch (error) {
        if (this.analiseAtiva !== requisicao
          || !this.contextoPermaneceValido(contextoInicial, false)) return;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao analisar CSV',
            text: error.message || 'Não foi possível analisar o arquivo.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        if (this.analiseAtiva === requisicao) {
          this.analiseAtiva = null;
          this.analisando = false;
        }
      }
    },
    // Bloqueio de venda sem estoque — mesma regra da home (ModalEmbalagens):
    // preferência ligada OU perfil Representante. O depósito consultado segue
    // o tipo do pedido (doação WD → DOA; demais → LUI/LUN).
    async marcarSemEstoque(preview) {
      const encontrados = (preview.itens || []).filter((p) => p.status === 'encontrado' && p.item_id);
      if (!encontrados.length) return;
      const bloqueados = await validarEstoqueItens(
        encontrados.map((p) => ({ item_id: p.item_id, quantidade: p.quantidade })),
      );
      if (!bloqueados.size) return;
      let total = 0;
      encontrados.forEach((p) => {
        if (bloqueados.has(String(p.item_id))) {
          p.status = 'sem_estoque';
          total += 1;
        }
      });
      if (!total) return;
      if (preview.resumo) {
        preview.resumo.encontrados = Math.max(0, Number(preview.resumo.encontrados || 0) - total);
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Estoque insuficiente',
          text: `${total} item(ns) sem estoque disponível (ou sem informação de estoque) não serão adicionados.`,
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      });
    },
    // Preço exibido na revisão = mesmo cálculo do carrinho/pedido: simulação de
    // imposto (POST imposto/simular) respeitando o tipo do pedido do InfoPedido
    // (doação WD usa d-preco-doacao; venda usa deVlDesconto). Guarda o preço de
    // tabela em preco_base para os fluxos de adição reaproveitarem (cache da simulação).
    async simularPrecosPreview(preview, contexto, requisicao) {
      const formData = this.formInfoPedido;
      const itens = (preview.itens || []).filter((p) => p.status === 'encontrado' && p.item_id && p.preco != null);
      let todosValidados = true;
      for (const p of itens) {
        const precoBase = this.precoValidado(p.preco);
        p.preco_base = precoBase;
        let precoFinal = null;
        if (precoBase != null) try {
          // eslint-disable-next-line no-await-in-loop
          const simulado = await simularImpostoUnitario({
            formData,
            itemId: p.item_id,
            precoBase,
            comProveniencia: true,
          });
          precoFinal = this.precoValidado(simulado?.preco);
          p.descontoConfirmado = simulado?.descontoConfirmado === true;
        } catch (e) {
          precoFinal = null;
        }
        if (this.analiseAtiva !== requisicao || !this.contextoPermaneceValido(contexto, true)) {
          return false;
        }
        if (precoFinal == null) {
          todosValidados = false;
          this.marcarErroImposto(p, preview);
        } else {
          p.preco = precoFinal;
        }
      }
      if (!todosValidados) this.notificarImpostoIndisponivel();
      return todosValidados;
    },
    voltar() {
      if (this.analisando || this.processando) return;
      this.preview = null;
    },
    async adicionarAoCarrinho() {
      if (this.analisando || this.processando || !this.previewPronto) return;
      const previewAtual = this.preview;
      const encontrados = this.preview.itens.filter((p) => p.status === 'encontrado');
      if (!encontrados.length) return;
      const requisicao = ++this.proximaRequisicao;
      const contexto = this.capturarContexto(true);
      this.processamentoAtivo = requisicao;
      this.processando = true;
      try {
        if (this.contexto === 'operacao') {
          await this.adicionarOperacao(encontrados, contexto, requisicao, previewAtual);
          return;
        }
        const formData = this.formInfoPedido;
        const clienteNome = formData?.cliente?.clientenomeabreviado || '';
        const seqInicial = Number(this.$store.getters['PedidosStore/getLastSequenceItemPedido'] || 0);
        const resultados = await Promise.all(encontrados.map(async (p, indice) => {
          const precoBase = this.precoValidado(p.preco_base);
          let precoComImposto = null;
          try {
            const simulado = await simularImpostoUnitario({
              formData,
              itemId: p.item_id,
              precoBase,
              comProveniencia: true,
            });
            precoComImposto = this.precoValidado(simulado?.preco);
            p.precoTabelaErp = simulado?.precoTabelaErp;
          } catch (e) {
            precoComImposto = null;
          }
          if (precoBase == null || precoComImposto == null) return { item: p, cartItem: null };
          const quantidade = parseInt(p.quantidade, 10) || 0;
          return { item: p, cartItem: {
            cod_marca: p.cod_marca,
            familia_comercial_id: p.familia_comercial_id,
            familia_comercial_descricao: p.familia_comercial_descricao,
            cdn_cor: p.cdn_cor,
            cdn_tamanho: p.cdn_tamanho,
            des_tamanho: p.des_tamanho,
            des_cor: p.des_cor,
            item_id: p.item_id,
            item_descricao: p.item_descricao || p.familia_comercial_descricao,
            item_imagem: IMAGEM_PADRAO,
            pedido_item_seq: seqInicial + ((indice + 1) * 10),
            pic_id: 0,
            marca: p.marca,
            embalagem: p.embalagem,
            cdn_embalag: p.cdn_embalagem,
            pedido_item_preco_unitario: precoComImposto,
            pedido_item_preco_tabela_erp: p.precoTabelaErp,
            pedido_item_preco_unitario_base: precoBase,
            pedido_item_quantidade: quantidade,
            valor_total: +(precoComImposto * quantidade),
            valorTotalComImposto: +(precoComImposto * quantidade),
            pedido_item_preco_total: +(precoComImposto * quantidade),
            precoComImposto,
            cliente: clienteNome,
            itemMultiplo: Number(p.item_lote_multiplo) > 1 ? Number(p.item_lote_multiplo) : null,
            ...(this.tipoPedidoSelecionado
              ? { categoria_tipo_pedido: this.tipoPedidoSelecionado }
              : {}),
          } };
        }));
        if (this.processamentoAtivo !== requisicao
          || this.preview !== previewAtual
          || !this.contextoPermaneceValido(contexto, true)) return;
        const invalidos = resultados.filter((r) => !r.cartItem);
        if (invalidos.length) {
          invalidos.forEach((r) => this.marcarErroImposto(r.item, previewAtual));
          this.$set(previewAtual, 'precosValidados', false);
          this.notificarImpostoIndisponivel();
          return;
        }
        const cartItens = resultados.map((r) => r.cartItem);

        await this.hidratarImagensCarrinho(cartItens);
        if (this.processamentoAtivo !== requisicao
          || this.preview !== previewAtual
          || !this.contextoPermaneceValido(contexto, true)) return;

        const pedidoAtual = this.$store.getters['PedidosStore/getPedido'] || {};
        if (!Array.isArray(pedidoAtual.pedidoItens)) {
          this.$store.commit('PedidosStore/SET_PEDIDO', { ...pedidoAtual, pedidoItens: [] });
        }
        cartItens.forEach((item) => {
          this.$store.commit('PedidosStore/ADD_OR_UPDATE_PRODUCT_ITEM_PEDIDO', item);
        });

        const semImposto = this.preview.itens.length - encontrados.length;
        const aviso = semImposto > 0 ? ` ${semImposto} item(ns) sem preço/não encontrado(s) foram ignorados.` : '';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Itens adicionados ao carrinho',
            text: `${cartItens.length} produto(s) adicionado(s).${aviso}`,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });

        this.show = false;
        this.$router.push({ name: 'app-carrinho-compra' });
      } catch (error) {
        if (this.processamentoAtivo !== requisicao
          || this.preview !== previewAtual
          || !this.contextoPermaneceValido(contexto, true)) return;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao adicionar itens',
            text: error.message || 'Não foi possível adicionar os itens ao carrinho.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        if (this.processamentoAtivo === requisicao) {
          this.processamentoAtivo = null;
          this.processando = false;
        }
      }
    },
    // Operações → Pedidos de Venda: adiciona os itens à lista do pedido
    // (intensPedido/setListTabelaItens), agrupando por referência — mesmo
    // formato do Auto Completar Pedido.
    async adicionarOperacao(encontrados, contexto, requisicao, previewAtual) {
        const formData = this.formInfoPedido;
        const tabelaprecoid = this.precoId
          || (formData.dataCapaPedido && formData.dataCapaPedido.tabelaprecoid)
          || null;

        const grupos = {};
        let seqGrupo = 0;
        for (const p of encontrados) {
          const precoBase = this.precoValidado(p.preco_base);
          let precoFinal = null;
          try {
            // eslint-disable-next-line no-await-in-loop
            const simulado = await simularImpostoUnitario({
              formData,
              itemId: p.item_id,
              precoBase,
              comProveniencia: true,
            });
            precoFinal = this.precoValidado(simulado?.preco);
            p.precoTabelaErp = simulado?.precoTabelaErp;
          } catch (e) {
            precoFinal = null;
          }
          if (this.processamentoAtivo !== requisicao
            || this.preview !== previewAtual
            || !this.contextoPermaneceValido(contexto, true)) return;
          if (precoBase == null || precoFinal == null) {
            this.marcarErroImposto(p, previewAtual);
            this.$set(previewAtual, 'precosValidados', false);
            this.notificarImpostoIndisponivel();
            return;
          }
          const qtd = parseInt(p.quantidade, 10) || 0;
          const ref = p.familia_comercial_id;
          if (!grupos[ref]) {
            seqGrupo += 1;
            grupos[ref] = {
              CodMarca: p.cod_marca || null,
              DescricaoMarca: p.marca || '',
              CodReferencia: ref,
              DescricaoReferencia: p.familia_comercial_descricao,
              referencia: `${ref}-${p.familia_comercial_descricao}`,
              Qtde: 0,
              PrecoUnitario: precoFinal,
              ValorTotal: 0,
              PrecoComImposto: precoFinal,
              BaseIcms: 0,
              ValorIcms: 0,
              sequencia: seqGrupo,
              itens: [],
              editar: {
                cod_marca: p.cod_marca || null,
                cod_categ: null,
                cod_referencia: ref,
                precoId: tabelaprecoid,
              },
            };
          }
          const grupo = grupos[ref];
          grupo.Qtde += qtd;
          grupo.ValorTotal = +(grupo.ValorTotal + precoFinal * qtd).toFixed(2);
          grupo.itens.push({
            codMarca: p.cod_marca || null,
            familiaComercialId: ref,
            cdncor: p.cdn_cor,
            des_cor: p.des_cor,
            cdn_tamanho: p.des_tamanho,
            itemId: p.item_id,
            item_descricao: `${p.item_descricao || p.familia_comercial_descricao} ${p.des_tamanho || ''} ${p.des_cor || ''}`.trim(),
            descricao_referencia: p.familia_comercial_descricao,
            pedidoItemQuantidade: qtd,
            marca: p.marca || '',
            embalagem: p.embalagem,
            cdn_embalagem: p.cdn_embalagem,
            precoUnitario: precoFinal,
            precoTabelaErp: p.precoTabelaErp,
            valorTotal: +(precoFinal * qtd).toFixed(2),
            pedidoItemQuantidadeSaldo: 0,
            pedidoItemQuantidadeFaturada: 0,
            pedidoitemseq: 0,
            desc_tamanho: p.des_tamanho,
            precoComImposto: precoFinal,
            baseIcms: 0,
            valorIcms: 0,
            itemMultiplo: Number(p.item_lote_multiplo) > 1 ? Number(p.item_lote_multiplo) : 1,
            naturezaoperacaoidimposto: null,
            valorTotalComImposto: +(precoFinal * qtd).toFixed(2),
          });
        }

        if (this.processamentoAtivo !== requisicao
          || this.preview !== previewAtual
          || !this.contextoPermaneceValido(contexto, true)) return;
        const listaAtual = this.$store.getters['intensPedido/getlistarTabelaItens'] || [];

        Object.keys(grupos).forEach((ref) => {
          const grupoNovo = grupos[ref];
          const existente = listaAtual.find(
            (g) => g.referencia === grupoNovo.referencia && g.CodReferencia === grupoNovo.CodReferencia,
          );

          if (!existente) {
            this.$store.dispatch('intensPedido/setListTabelaItens', grupoNovo);
            return;
          }

          const grupoMesclado = {
            ...existente,
            itens: (existente.itens || []).map((sub) => ({ ...sub })),
          };

          grupoNovo.itens.forEach((subNovo) => {
            const subExistente = grupoMesclado.itens.find((sub) => sub.itemId === subNovo.itemId);
            if (subExistente) {
              const novaQtd = (Number(subExistente.pedidoItemQuantidade) || 0)
                + (Number(subNovo.pedidoItemQuantidade) || 0);
              subExistente.pedidoItemQuantidade = novaQtd;
              subExistente.valorTotal = +((Number(subExistente.precoUnitario) || 0) * novaQtd).toFixed(2);
              subExistente.valorTotalComImposto = +((Number(subExistente.precoComImposto) || 0) * novaQtd).toFixed(2);
            } else {
              grupoMesclado.itens.push({ ...subNovo });
            }
          });

          grupoMesclado.Qtde = grupoMesclado.itens.reduce(
            (acc, sub) => acc + (Number(sub.pedidoItemQuantidade) || 0),
            0,
          );
          grupoMesclado.ValorTotal = +grupoMesclado.itens.reduce(
            (acc, sub) => acc + (Number(sub.valorTotal) || 0),
            0,
          ).toFixed(2);

          this.$store.dispatch('intensPedido/setListTabelaItens', grupoMesclado);
        });

        const semAdicionar = this.preview.itens.length - encontrados.length;
        const aviso = semAdicionar > 0 ? ` ${semAdicionar} item(ns) não adicionado(s) (sem preço, não encontrado ou categoria diferente).` : '';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Itens adicionados ao pedido',
            text: `${encontrados.length} produto(s) adicionado(s).${aviso}`,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.show = false;
    },
    async hidratarImagensCarrinho(cartItens) {
      try {
        const pares = new Map();
        cartItens.forEach((ci) => {
          if (ci.cod_marca && ci.familia_comercial_id) {
            pares.set(`${ci.cod_marca}::${ci.familia_comercial_id}`, {
              cod_marca: ci.cod_marca,
              familia_comercial_id: ci.familia_comercial_id,
            });
          }
        });
        if (!pares.size) return;
        const client = new EsMarcaClient();
        const mapaImg = new Map();
        await Promise.all([...pares.values()].map(async (par) => {
          try {
            const resp = await client.listarImagensPorMarca(par.cod_marca, {
              familia_comercial_id: par.familia_comercial_id,
            });
            const data = Array.isArray(resp) ? resp : resp?.data || [];
            const full = data[0];
            const imgs = (full && full.imagens) || [];
            if (!imgs.length) return;
            const principal = imgs.find((i) => i.imagemPrincipal === 1) || imgs[0];
            if (principal && principal.caminho) {
              mapaImg.set(`${par.cod_marca}::${par.familia_comercial_id}`, principal.caminho);
            }
          } catch (e) {
            // best-effort: mantém a imagem padrão deste item
          }
        }));
        cartItens.forEach((ci) => {
          const caminho = mapaImg.get(`${ci.cod_marca}::${ci.familia_comercial_id}`);
          if (caminho) ci.item_imagem = caminho;
        });
      } catch (e) {
        console.error('Erro ao hidratar imagens do carrinho (importar CSV):', e);
      }
    },
    onHidden() {
      this.invalidarSessao();
      this.preview = null;
      this.linhasValidas = [];
      this.linhasInvalidas = 0;
      this.analisando = false;
      this.processando = false;
      if (typeof this.escolhaCategoria.resolve === 'function') this.escolhaCategoria.resolve(null);
      this.escolhaCategoria = {
        visivel: false, opcoes: [], contagem: {}, selecionada: null, resolve: null,
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
  },
};
</script>
